import { paletteFor, type Priority } from "@app/core";
import { washStyle } from "../../../features/block-wash.js";
import { useBlockStyle } from "../../../features/useBlockStyle.js";
import { useI18n } from "../../../lib/useI18n.js";
import { useThemeStore } from "../../../stores/theme.store.js";

interface SampleBlock {
  title: string;
  time: string;
  custom: string | null;
  context: string | null;
  priority: Priority;
  isEvent: boolean;
}

/** A lightweight live calendar preview that re-renders from the working theme.
 *  Chrome (card/surface/text) reflects the injected CSS vars automatically; the
 *  sample blocks use the same `washStyle` the real calendar does, with colors
 *  drawn from the categorical palette so topics read as distinct. */
export function ThemePreview() {
  const { locale, t } = useI18n();
  const config = useThemeStore((s) => s.config);
  const { tint, gradient } = useBlockStyle();
  const palette = paletteFor(config);

  const blocks: SampleBlock[] = [
    { title: "Standup", time: "9:00", custom: null, context: palette[0] ?? null, priority: "none", isEvent: true },
    { title: "Write report", time: "11:30", custom: null, context: palette[3] ?? null, priority: "high", isEvent: false },
    { title: "Design review", time: "14:00", custom: null, context: palette[6] ?? null, priority: "none", isEvent: true },
    { title: "Plan trip", time: "16:00", custom: palette[9] ?? "#3b82f6", context: null, priority: "low", isEvent: false },
  ];

  return (
    <div className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-medium">
          {new Date().toLocaleDateString(locale, { weekday: "long", month: "short", day: "numeric" })}
        </span>
        <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">{t("common.today")}</span>
      </div>
      <div className="flex flex-col gap-1.5 p-3">
        {blocks.map((b) => {
          const { vars } = washStyle(
            { priority: b.priority, custom_color: b.custom, is_event: b.isEvent },
            b.context,
            tint,
            gradient,
          );
          return (
            <div
              key={b.title}
              className="task-wash flex items-center justify-between rounded-md border px-2 py-1.5 text-xs"
              style={vars}
            >
              <span className="font-medium">{b.title}</span>
              <span className="opacity-80">{b.time}</span>
            </div>
          );
        })}
        <div className="mt-1 flex flex-wrap gap-1">
          {palette.slice(0, 8).map((hex, i) => (
            <span key={i} className="h-4 w-4 rounded-full border" style={{ background: hex }} />
          ))}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{t("theme.previewMuted")}</p>
      </div>
    </div>
  );
}
